import { Box, Button, Flex, Text } from "@chakra-ui/react";
import Image from "next/image";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useAccount, useSwitchChain } from "wagmi";

import {
  APPROVE_CTA,
  CONNECT_WALLET_CTA,
  isNumber,
  toFixedWithoutRounding,
  UNSTAKE_NOW,
} from "@constants/common";
import { EVENTS } from "@constants/reduxEvents";

import LstTokenSelect from "@components/LstTokenSelect";
import TicketInput from "@components/TicketInput";
import TicketInputRightSection from "@components/TicketInputRightSection";

import {
  APPROVE_UNSTAKING,
  CONNECT_WALLET,
  UNSTAKE_CTA,
} from "@constants/analytics";
import { ERDataType } from "@hooks/useExchangeRate";
import {
  emitReduxEvent,
  formatNumber,
  numberToCurrency,
  trackEvent,
} from "../../utils";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import useDappChain from "@hooks/useDappChain";
import { ETH, ETHX } from "@config/contractsConfig";
import useTokenBalance from "@hooks/useTokenBalance";
import useDappConfig from "@hooks/useDappConfig";

import { updateUserData } from "store";
import useUnstakeEstimate from "@hooks/useUnstakeEstimate";
import useUnstakeInfo from "@hooks/useUnstakeInfo";
import useUnstakeTokenAllowance from "@hooks/useUnstakeTokenAllowance";

const MAX_FRACTION_DIGITS = 4;

interface StakeComponentProps {
  conversionData: ERDataType;
  isActive: boolean;
}

const UnstakeComponent = ({
  conversionData,
  isActive,
}: StakeComponentProps) => {
  const [unstakeAmount, setUnstakeAmount] = useState("");
  const [error, setError] = useState("");

  const { selectedERC20 } = useSelector((state: any) => state.user);

  const conversions = useMemo(() => {
    const conversionToERC20 = conversionData?.exchangeRate;
    return {
      toERC20: conversionToERC20 || 0,
      fromERC20: conversionToERC20 ? 1 / conversionToERC20 : 0,
    };
  }, [conversionData?.exchangeRate]);

  const { switchChain } = useSwitchChain();

  const { chain } = useDappChain();

  const { allowance } = useUnstakeTokenAllowance();

  const { isConnected, address } = useAccount();

  const { xTokenTokenBalance } = useTokenBalance();

  const { openConnectModal } = useConnectModal();

  const { config, isWithdrawSupported, withdrawEnabledChains } =
    useDappConfig();

  const { returns } = useUnstakeEstimate(unstakeAmount);

  const { minAmountToWithdraw, withdrawalableAmount } = useUnstakeInfo();

  const checkInput = useCallback(
    (value = unstakeAmount.trim()) => {
      if (!isConnected) return "";
      let error = "";
      if (!+value || Number.isNaN(+value)) {
        error =
          "This field can't be blank or zero. Please provide valid input.";
      } else if (Number(value) > xTokenTokenBalance) {
        error = `You can't unstake more than available balance in your wallet.`;
      } else if (Number(unstakeAmount) < Number(minAmountToWithdraw)) {
        error = `Maximum withdrawal allowed for ${selectedERC20} ${minAmountToWithdraw}.`;
      } else if (Number(unstakeAmount) > Number(withdrawalableAmount)) {
        error = `Maximum withdrawal allowed for ${selectedERC20} is ${minAmountToWithdraw} .`;
      }
      return error;
    },
    [
      isConnected,
      minAmountToWithdraw,
      selectedERC20,
      unstakeAmount,
      withdrawalableAmount,
      xTokenTokenBalance,
    ]
  );

  useEffect(() => {
    if (unstakeAmount !== "") {
      const error = checkInput(unstakeAmount);
      setError(error);
    }
  }, [address, unstakeAmount, checkInput]);

  const dispatch = useDispatch();

  useEffect(() => {
    if (!isActive) {
      return;
    }
    if (selectedERC20) {
      if (selectedERC20 in config.tokens) {
        return;
      }

      dispatch(
        updateUserData({
          selectedERC20: ETHX,
        })
      );
    }
  }, [chain.id, config.tokens, dispatch, isActive, selectedERC20]);

  const isApproved = useMemo(() => {
    const amount = unstakeAmount || "";
    return (
      allowance && Number(allowance) && Number(allowance) >= Number(amount)
    );
  }, [allowance, unstakeAmount]);

  const handleChange = (value: string) => {
    const val = value.trim().replace(/,/g, "");
    if (isNumber(val) || val === "") {
      if (val.indexOf(".") === -1) {
        setUnstakeAmount(val);
      } else {
        if (val.length - (val.indexOf(".") + 1) <= MAX_FRACTION_DIGITS) {
          setUnstakeAmount(val === "." ? "0." : val);
        }
      }
    }
  };

  const getButtonText = () => {
    if (!isConnected) {
      return CONNECT_WALLET_CTA;
    } else {
      if (!isWithdrawSupported) {
        return `Switch to ${withdrawEnabledChains[0].name} to Unstake`;
      } else if (isApproved) {
        return UNSTAKE_NOW;
      }
      return APPROVE_CTA;
    }
  };

  const handleClick = useCallback(() => {
    if (!isConnected) {
      // emitReduxEvent(EVENTS.OPEN_WALLET_MODAL);
      openConnectModal?.();
      trackEvent(CONNECT_WALLET, {
        type: "center_wiget",
      });
    } else {
      if (!isWithdrawSupported) {
        switchChain({ chainId: withdrawEnabledChains[0].id });
      } else if (isApproved) {
        if (!unstakeAmount) {
          setError(
            "This field can't be blank or zero. Please provide valid input."
          );
          return;
        }
        trackEvent(UNSTAKE_CTA, {
          amount: unstakeAmount,
        });

        emitReduxEvent(EVENTS.HANDLE_UNSTAKE, {
          amount: unstakeAmount,
          asset:
            selectedERC20 !== ETH
              ? config.tokens[selectedERC20].address
              : config.nativeToken.address,
        });
        setUnstakeAmount("");
      } else {
        emitReduxEvent(EVENTS.HANDLE_XTOKEN_APPROVE);
        trackEvent(APPROVE_UNSTAKING);
      }
    }
  }, [
    isConnected,
    openConnectModal,
    isWithdrawSupported,
    isApproved,
    switchChain,
    withdrawEnabledChains,
    unstakeAmount,
    selectedERC20,
    config.tokens,
    config.nativeToken.address,
  ]);

  const handleMaxClick = useCallback(() => {
    setUnstakeAmount(toFixedWithoutRounding(xTokenTokenBalance.toString(), 4));
  }, [xTokenTokenBalance]);

  return (
    <Box pos="relative">
      <Box
        pos="relative"
        fontSize="14px"
        lineHeight="20px"
        fontWeight="400"
        color="textSecondary"
        pointerEvents={isWithdrawSupported || !address ? "all" : "none"}
        filter="auto"
        blur={isWithdrawSupported || !address ? "0" : "3px"}
      >
        <LstTokenSelect hideTags hideETH title="Withdraw rsETH as" />
        <Flex w="100%" alignItems="center">
          <Flex
            justifyContent="space-between"
            flexGrow={1}
            flexDir={{ base: "column", xl: "row" }}
          >
            <Box
              display={{ base: isConnected ? "none" : "inherit", xl: "block" }}
              h="20px"
            >
              <Text>Enter amount of rsETH</Text>
            </Box>
          </Flex>
        </Flex>

        <TicketInput
          inputRightElement={
            <TicketInputRightSection
              isConnected={isConnected}
              tokenAmount={xTokenTokenBalance}
              onClick={handleMaxClick}
              token={config.xtoken.symbol}
            />
          }
          value={unstakeAmount}
          onChange={(value: string) => handleChange(value)}
          error={error}
        />

        {error && (
          <Flex alignItems="center" mt="8px">
            <Image
              src="/assets/transactions/error.svg"
              width={20}
              height={20}
              alt="error-icon"
            />
            <Text ml="0.5rem" color="error" fontSize="12px" fontWeight="400">
              {error ? error : "error"}
            </Text>
          </Flex>
        )}

        <Box mt={{ base: "2rem", md: "1.5rem" }}>
          <Flex justifyContent="space-between">
            <Text>You will get</Text>
            <Text>
              {Number(returns)
                ? formatNumber(
                    Number(returns),
                    false,
                    MAX_FRACTION_DIGITS,
                    true
                  )
                : formatNumber(
                    Number(returns),
                    false,
                    MAX_FRACTION_DIGITS,
                    true
                  ) || 0}{" "}
              {selectedERC20}
            </Text>
          </Flex>

          {conversions.toERC20 ? (
            <Flex justifyContent="space-between" mt="1rem">
              <Text>Exchange rate</Text>
              <Text>
                1 {config.xtoken.symbol} ={" "}
                {toFixedWithoutRounding(conversions.toERC20.toString(), 4)}{" "}
                {selectedERC20}
              </Text>
            </Flex>
          ) : null}
          {isWithdrawSupported && withdrawalableAmount ? (
            <Flex justifyContent="space-between" mt="1rem">
              <Text>Maximum Withdraw</Text>
              <Text>
                {numberToCurrency(withdrawalableAmount.toString())}{" "}
                {selectedERC20}
              </Text>
            </Flex>
          ) : null}
        </Box>
      </Box>
      <Button
        borderRadius="12px"
        bg="textPrimary"
        p="16px 64px"
        h="56px"
        mt="12px"
        width="100%"
        _hover={{
          bg: "textPrimary",
        }}
        onClick={handleClick}
        isDisabled={!!error}
      >
        <Text
          fontSize="16px"
          lineHeight="24px"
          fontWeight="600"
          color="backgroundCard"
        >
          {getButtonText()}
        </Text>
      </Button>
    </Box>
  );
};

export default UnstakeComponent;
